'use client'

import { cn } from '@/lib/utils'
import {
  convertToUserTimezone,
  formatDateInUserTimezone,
  formatRelativeTimeInUserTimezone,
  formatTimeInUserTimezone,
  getSmartRelativeTime,
  getUserTimezone
} from '@/lib/utils/timezone'
import { useSession } from 'next-auth/react'
import { useMemo } from 'react'

export interface TimeDisplayProps {
  /** UTC datetime string (ISO format) */
  utcDateTime: string | Date | null | undefined
  /** User's timezone - falls back to session timezone or 'America/Edmonton' */
  userTimezone?: string
  /** Display format type */
  format?: 'time' | 'date' | 'datetime' | 'relative' | 'smart-relative' | 'short-date' | 'long-date' | 'full'
  /** Whether to show timezone indicator */
  showTimezone?: boolean
  /** Additional CSS classes */
  className?: string
  /** Fallback text when date is invalid */
  fallback?: string
  /** Whether to show "just now" for very recent times */
  showJustNow?: boolean
  /** Custom format string (overrides format prop) */
  customFormat?: string
  /** Whether to show relative time in tooltip */
  showRelativeTooltip?: boolean
}

/**
 * TimeDisplay - Display times in user's timezone with various formatting options
 *
 * Features:
 * - Automatic timezone conversion from UTC
 * - Multiple display formats including smart relative time
 * - Session integration for timezone preferences
 * - Accessibility support with proper ARIA labels
 * - Timezone indicators
 * - Graceful error handling
 */
export function TimeDisplay({
  utcDateTime,
  userTimezone,
  format = 'datetime',
  showTimezone = false,
  className,
  fallback = 'N/A',
  showJustNow = true,
  customFormat,
  showRelativeTooltip = false
}: TimeDisplayProps) {
  const { data: session } = useSession()

  // Determine the timezone to use with smart fallback
  const timezone = useMemo(() => {
    return getUserTimezone(
      userTimezone,
      session?.user?.preferences?.timezone,
      'America/Edmonton'
    )
  }, [userTimezone, session?.user?.preferences?.timezone])

  // Format the main display time
  const formattedTime = useMemo(() => {
    if (!utcDateTime) return fallback

    try {
      // Convert to string if it's a Date object
      const dateString = utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime

      // Validate the date string
      if (!dateString || dateString === 'Invalid Date') return fallback

      const userTime = convertToUserTimezone(dateString, timezone)

      // Check if date is valid
      if (!userTime.isValid()) return fallback

      // Use custom format if provided
      if (customFormat) {
        return formatDateInUserTimezone(dateString, timezone, customFormat)
      }

      switch (format) {
        case 'time':
          return formatTimeInUserTimezone(dateString, timezone, 'h:mm A')

        case 'date':
          return formatDateInUserTimezone(dateString, timezone, 'MMM D, YYYY')

        case 'short-date':
          return formatDateInUserTimezone(dateString, timezone, 'MMM D')

        case 'long-date':
          return formatDateInUserTimezone(dateString, timezone, 'dddd, MMMM D, YYYY')

        case 'datetime':
          return formatDateInUserTimezone(dateString, timezone, 'MMM D, YYYY h:mm A')

        case 'full':
          return formatDateInUserTimezone(dateString, timezone, 'dddd, MMMM D, YYYY [at] h:mm A')

        case 'relative':
          return formatRelativeTimeInUserTimezone(dateString, timezone)

        case 'smart-relative':
          return getSmartRelativeTime(dateString, timezone)

        default:
          return formatDateInUserTimezone(dateString, timezone, 'MMM D, YYYY h:mm A')
      }
    } catch (error) {
      console.warn('TimeDisplay: Error formatting date', { utcDateTime, timezone, error })
      return fallback
    }
  }, [utcDateTime, timezone, format, fallback, customFormat])

  // Create timezone indicator
  const timezoneIndicator = useMemo(() => {
    if (!showTimezone || !utcDateTime) return ''

    try {
      const dateString = utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime
      const userTime = convertToUserTimezone(dateString, timezone)
      return ` (${userTime.format('z')})`
    } catch {
      return ''
    }
  }, [utcDateTime, timezone, showTimezone])

  // Create relative time for tooltip
  const relativeTime = useMemo(() => {
    if (!showRelativeTooltip || !utcDateTime || format === 'relative' || format === 'smart-relative') return ''

    try {
      const dateString = utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime
      return getSmartRelativeTime(dateString, timezone)
    } catch {
      return ''
    }
  }, [utcDateTime, timezone, showRelativeTooltip, format])

  // Create accessible label
  const ariaLabel = useMemo(() => {
    if (!utcDateTime || formattedTime === fallback) return fallback

    try {
      const dateString = utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime
      const fullDateTime = formatDateInUserTimezone(dateString, timezone, 'dddd, MMMM D, YYYY [at] h:mm A')
      const relativeText = relativeTime ? ` (${relativeTime})` : ''
      return `${fullDateTime}${showTimezone ? ` in ${timezone}` : ''}${relativeText}`
    } catch {
      return formattedTime
    }
  }, [utcDateTime, timezone, formattedTime, fallback, showTimezone, relativeTime])

  // Create tooltip text
  const tooltipText = useMemo(() => {
    if (!utcDateTime || formattedTime === fallback) return undefined

    const parts = []

    // Add full datetime if not already showing it
    if (format !== 'full' && format !== 'datetime') {
      try {
        const dateString = utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime
        const fullDateTime = formatDateInUserTimezone(dateString, timezone, 'dddd, MMMM D, YYYY [at] h:mm A')
        parts.push(fullDateTime)
      } catch {
        // Ignore error
      }
    }

    // Add relative time if enabled and not already showing it
    if (relativeTime && format !== 'relative' && format !== 'smart-relative') {
      parts.push(relativeTime)
    }

    // Add timezone info if enabled
    if (showTimezone) {
      parts.push(`Timezone: ${timezone}`)
    }

    return parts.length > 0 ? parts.join(' • ') : undefined
  }, [utcDateTime, timezone, formattedTime, fallback, format, relativeTime, showTimezone])

  return (
    <time
      dateTime={utcDateTime instanceof Date ? utcDateTime.toISOString() : utcDateTime || undefined}
      aria-label={ariaLabel}
      className={cn('text-inherit cursor-help', className)}
      title={tooltipText}
    >
      {formattedTime}{timezoneIndicator}
    </time>
  )
}

/**
 * Convenience component for displaying just the time
 */
export function TimeOnly(props: Omit<TimeDisplayProps, 'format'>) {
  return <TimeDisplay {...props} format="time" />
}

/**
 * Convenience component for displaying just the date
 */
export function DateOnly(props: Omit<TimeDisplayProps, 'format'>) {
  return <TimeDisplay {...props} format="date" />
}

/**
 * Convenience component for displaying relative time
 */
export function RelativeTime(props: Omit<TimeDisplayProps, 'format'>) {
  return <TimeDisplay {...props} format="smart-relative" />
}

/**
 * Convenience component for displaying full date and time
 */
export function FullDateTime(props: Omit<TimeDisplayProps, 'format'>) {
  return <TimeDisplay {...props} format="full" showTimezone={true} />
}
